import { useEffect, useMemo, useState } from "react";
import { LayoutGridIcon, SearchIcon } from "lucide-react";
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Input,
} from "@devhop/ui";
import { resolveQiBaseUnitPrice } from "../lib/qi-line-pricing";
import {
  useQiCatalogCategoriesQuery,
  useQiCatalogProductsQuery,
} from "../api/queries";
import { QiCatalogCardImage, QiCatalogCardSkeleton } from "./QiCatalogCardImage";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";

const PAGE_SIZE = 24;
const ALL_CATEGORIES = "__all__";

type QiProductCatalogDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedId?: string;
  onSelect: (product: QiCatalogProductDTO) => void;
};

export function QiProductCatalogDialog({
  open,
  onOpenChange,
  selectedId,
  onSelect,
}: QiProductCatalogDialogProps) {
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [categoryId, setCategoryId] = useState(ALL_CATEGORIES);
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    if (!open) return;
    const timer = window.setTimeout(() => {
      setSearch(searchInput.trim());
      setOffset(0);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [searchInput, open]);

  useEffect(() => {
    if (!open) {
      setSearchInput("");
      setSearch("");
      setCategoryId(ALL_CATEGORIES);
      setOffset(0);
    }
  }, [open]);

  const categories = useQiCatalogCategoriesQuery();
  const products = useQiCatalogProductsQuery({
    q: search || undefined,
    categoryId: categoryId === ALL_CATEGORIES ? undefined : categoryId,
    limit: PAGE_SIZE,
    offset,
    enabled: open,
  });

  const categoryOptions = useMemo(
    () => [
      { value: ALL_CATEGORIES, label: "ທຸກຫມວດຫມູ່" },
      ...(categories.data?.items ?? []).map((row) => ({
        value: row.id,
        label: `${row.code} — ${row.name}`,
      })),
    ],
    [categories.data?.items],
  );

  const rows = products.data?.data ?? [];
  const total = products.data?.meta.total ?? 0;
  const hasMore = offset + PAGE_SIZE < total;
  const hasPrev = offset > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] w-[calc(100vw-2rem)] max-w-[min(90rem,calc(100vw-2rem))] flex-col gap-0 overflow-hidden p-0 sm:max-w-[min(90rem,calc(100vw-2rem))]">
        <DialogHeader className="border-b px-6 py-4">
          <DialogTitle className="flex items-center gap-2">
            <LayoutGridIcon className="h-5 w-5" />
            ເລືອກສິນຄ້າຈາກ catalog
          </DialogTitle>
          <DialogDescription>
            ຄົ້ນຫາດ້ວຍຊື່, ລະຫັດ ຫຼື barcode — ເລືອກແຖວເພື່ອເພີ່ມລາຍການ
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 border-b px-6 py-3">
          <div className="flex flex-wrap gap-2">
            <div className="relative min-w-[220px] flex-1">
              <SearchIcon className="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="ຄົ້ນຫາ SKU..."
                className="h-9 pl-9"
              />
            </div>
            <SimpleSelect
              value={categoryId}
              onValueChange={(value) => {
                setCategoryId(value);
                setOffset(0);
              }}
              options={categoryOptions}
              className="h-9 min-w-[180px]"
            />
          </div>
          <p className="text-muted-foreground text-xs tabular-nums">
            {products.isLoading
              ? "ກຳລັງໂຫຼດ..."
              : `${rows.length}/${total.toLocaleString()} ລາຍການ`}
          </p>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
            {products.isLoading
              ? Array.from({ length: 10 }).map((_, key) => (
                  <QiCatalogCardSkeleton key={key} />
                ))
              : rows.map((product) => {
                  const selected = product.id === selectedId;
                  return (
                    <button
                      key={product.id}
                      type="button"
                      onClick={() => {
                        onSelect(product);
                        onOpenChange(false);
                      }}
                      className={`overflow-hidden rounded-xl border bg-card text-left transition hover:border-primary/50 hover:shadow-sm ${
                        selected ? "border-primary ring-2 ring-primary/20" : ""
                      }`}
                    >
                      <QiCatalogCardImage
                        src={product.imageKey}
                        alt={product.name}
                      />
                      <div className="space-y-1 p-3">
                        <Badge variant="outline" className="text-[10px]">
                          {product.code}
                        </Badge>
                        <p className="line-clamp-2 font-medium text-sm leading-snug">
                          {product.name}
                        </p>
                        <p className="text-muted-foreground text-xs">
                          {product.unit} ·{" "}
                          {resolveQiBaseUnitPrice(product).toLocaleString(undefined, {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 2,
                          })}
                        </p>
                      </div>
                    </button>
                  );
                })}
          </div>
          {!products.isLoading && rows.length === 0 ? (
            <p className="py-8 text-center text-muted-foreground text-sm">
              ບໍ່ພົບສິນຄ້າ
            </p>
          ) : null}
        </div>

        <div className="flex items-center justify-between border-t px-6 py-3">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={!hasPrev}
            onClick={() => setOffset((prev) => Math.max(0, prev - PAGE_SIZE))}
          >
            ກ່ອນໜ້າ
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={!hasMore}
            onClick={() => setOffset((prev) => prev + PAGE_SIZE)}
          >
            ຖັດໄປ
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
